import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { CheckCircle, XCircle, Filter, ArrowLeft } from 'lucide-react';
import { getQuestionsByExamId } from '../utils/questionAPI';
import { getSubjectById } from '../utils/subjectsAPI';
import { getLatestSubjectHistory } from '../utils/subjectHistorysAPI';

/**
 * 获取题型名称
 * @param {string} type - 题型代码
 * @returns {string} 题型名称
 */
const getTypeName = (type) => {
  switch(type) {
    case '1': 
    case 1:
    case '单选': return '单选题';
    
    case '2': 
    case 2:
    case '判断': return '判断题';
    
    case '3':
    case 3: 
    case '多选': return '多选题';
    
    default: return `题型${type}`;
  }
};

/**
 * 获取题型对应的样式类名
 * @param {string} type - 题型
 * @returns {string} 样式类名
 */
const getTypeStyle = (type) => {
  switch(type) {
    case '1': 
    case 1:
    case '单选': return 'bg-blue-50 text-blue-600 border-blue-200';
    
    case '2': 
    case 2:
    case '判断': return 'bg-green-50 text-green-600 border-green-200';
    
    case '3':
    case 3:
    case '多选': return 'bg-purple-50 text-purple-600 border-purple-200';
    
    default: return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};

/**
 * 统一题型标识
 * @param {string|number} type - 题型标识
 * @returns {string} 统一后的题型标识
 */
const normalizeType = (type) => {
  if (type === 1 || type === '1' || type === '单选') return '1';
  if (type === 2 || type === '2' || type === '判断') return '2';
  if (type === 3 || type === '3' || type === '多选') return '3';
  return type.toString();
};

/**
 * 考试答题详情页面组件
 * 用于展示考试答题详情，支持只看错题
 */
function ExamAnswerDetail() {
  const { examId, subjectId, historyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [examData, setExamData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [subject, setSubject] = useState(null);
  const [showWrongOnly, setShowWrongOnly] = useState(false);
  const [displayQuestions, setDisplayQuestions] = useState([]);
  
  /**
   * 获取问题ID
   * @param {Object} question - 问题对象
   * @returns {string} 问题ID
   */
  const getQuestionId = (question) => {
    return question._id || question.id;
  };
  
  /**
   * 判断问题是否答错
   * @param {Object} question - 问题对象
   * @returns {boolean} 是否答错
   */
  const isQuestionWrong = React.useCallback((question) => {
    const questionId = getQuestionId(question);
    const userAnswer = answers[questionId] || '';
    const correctOption = question.options?.find(opt => opt.value === "1");
    return !(correctOption && userAnswer === correctOption.code);
  }, [answers]);
  
  /**
   * 切换只看错题模式
   */
  const toggleWrongOnly = () => {
    setShowWrongOnly(!showWrongOnly);
  };
  
  useEffect(() => {
    // 当showWrongOnly变化时，更新显示的题目
    if (showWrongOnly) {
      setDisplayQuestions(questions.filter(q => isQuestionWrong(q)));
    } else {
      setDisplayQuestions(questions);
    }
  }, [showWrongOnly, questions, answers, isQuestionWrong]);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // 如果是从其他页面传递过来的数据
        if (location.state?.questions && location.state?.answers) {
          setQuestions(location.state.questions);
          setAnswers(location.state.answers);
          setSubject(location.state.subject);
          setExamData(location.state);
          setLoading(false);
          return;
        }
        
        // 否则从数据库加载数据
        // 加载科目信息
        const subjectData = await getSubjectById(subjectId);
        setSubject(subjectData);
        
        // 如果有历史ID，加载考试历史记录
        if (historyId) {
          const historyData = await getLatestSubjectHistory(historyId);
          
          // 加载题目信息
          const questionsData = await getQuestionsByExamId(subjectId, true);
          
          // 找出用户回答过的题目
          const filteredQuestions = questionsData.filter(q => 
            historyData.answers && historyData.answers[getQuestionId(q)]
          );
          
          setQuestions(filteredQuestions);
          setAnswers(historyData.answers || {});
          setExamData(historyData);
        } else {
          // 如果没有历史ID，找最近一次考试记录
          const userId = localStorage.getItem('user_id');
          
          if (!userId) {
            navigate('/login');
            return;
          }
          
          const historyData = await getLatestSubjectHistory(userId, examId, subjectId);
          
          // 加载题目信息
          const questionsData = await getQuestionsByExamId(subjectId, true);
          
          // 找出用户回答过的题目
          const filteredQuestions = questionsData.filter(q => 
            historyData.answers && historyData.answers[getQuestionId(q)]
          );
          
          setQuestions(filteredQuestions);
          setAnswers(historyData.answers || {});
          setExamData(historyData);
        }
        
      } catch (error) {
        console.error('Error loading exam details:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [location, examId, subjectId, historyId, navigate]);
  
  /**
   * 返回上一页
   */
  const handleBack = () => {
    navigate(-1);
  };
  
  /**
   * 获取统计信息
   * @returns {Object} 统计信息
   */
  const getStatistics = () => {
    if (!questions.length) return { total: 0, correct: 0, wrong: 0 };
    
    let correct = 0;
    let wrong = 0;
    
    questions.forEach(question => {
      const questionId = getQuestionId(question);
      const userAnswer = answers[questionId] || '';
      const correctOption = question.options?.find(opt => opt.value === "1");
      
      if (correctOption && userAnswer === correctOption.code) {
        correct++;
      } else {
        wrong++;
      }
    });
    
    return {
      total: questions.length,
      correct,
      wrong
    };
  };
  
  const stats = getStatistics();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header 
        title="答题详情" 
        leftContent={
          <button onClick={handleBack} className="p-1">
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
        }
        rightContent={
          <button 
            onClick={toggleWrongOnly}
            className={`flex items-center text-sm font-medium px-3 py-1 rounded-full ${
              showWrongOnly 
                ? 'bg-gray-200 text-gray-700'
                : 'bg-red-100 text-red-600'
            }`}
          >
            <Filter className="w-4 h-4 mr-1" />
            {showWrongOnly ? '全部题目' : '只看错题'}
          </button>
        }
      />
      
      <div className="p-4 flex-1">
        {/* 答题统计 */}
        <div className="bg-white rounded-lg p-4 mb-4 flex justify-between items-center">
          <div className="flex items-center">
            <div className="mr-4">
              <div className="text-sm text-gray-500">总题数</div>
              <div className="text-xl font-bold text-gray-800">{stats.total}</div>
            </div>
            <div className="border-l border-gray-200 pl-4 mr-4">
              <div className="text-sm text-green-500 flex items-center">
                <CheckCircle className="w-3 h-3 mr-1" />
                正确
              </div>
              <div className="text-xl font-bold text-green-600">{stats.correct}</div>
            </div>
            <div className="border-l border-gray-200 pl-4">
              <div className="text-sm text-red-500 flex items-center">
                <XCircle className="w-3 h-3 mr-1" />
                错误
              </div>
              <div className="text-xl font-bold text-red-500">{stats.wrong}</div>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-sm text-gray-500">正确率</div>
            <div className="text-xl font-bold text-primary">
              {stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0}%
            </div>
          </div>
        </div>
        
        {/* 答题详情 */}
        <div className="bg-white rounded-lg mb-4">
          <div className="p-4 border-b">
            <h3 className="font-medium text-lg">{showWrongOnly ? '错题详情' : '答题详情'}</h3>
            <div className="text-sm text-gray-500">
              {showWrongOnly 
                ? `共${stats.wrong}道错题` 
                : `共${stats.total}道题目，答对${stats.correct}道，答错${stats.wrong}道`}
            </div>
          </div>
          
          <div className="divide-y">
            {displayQuestions.length > 0 ? (
              displayQuestions.map((question, index) => {
                const questionId = getQuestionId(question);
                const userAnswer = answers[questionId] || '';
                const correctOption = question.options?.find(opt => opt.value === "1");
                const isCorrect = correctOption && userAnswer === correctOption.code;
                
                // 确定题型
                const questionType = normalizeType(question.typename || question.typecode || '未知');
                
                return (
                  <div key={questionId} className="p-4">
                    <div className="flex items-start">
                      <div className="mr-3 flex-shrink-0 bg-gray-100 text-gray-500 w-7 h-7 rounded-full flex items-center justify-center">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className={`mr-2 px-2 py-0.5 text-xs rounded-md ${getTypeStyle(questionType)}`}>
                            {getTypeName(questionType)}
                          </span>
                          <span className={`flex items-center text-sm ${isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                            {isCorrect ? (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                答对
                              </>
                            ) : (
                              <>
                                <XCircle className="w-4 h-4 mr-1" />
                                答错
                              </>
                            )}
                          </span>
                        </div>
                        
                        <div className="text-gray-800 mb-3">{question.title}</div>
                        
                        {question.content && (
                          <div className="text-sm text-gray-600 mb-3">
                            {question.content}
                          </div>
                        )}
                        
                        {/* 选项 */}
                        <div className="space-y-2 mb-2">
                          {question.options?.map((option) => (
                            <div 
                              key={option.code}
                              className={`p-2 border rounded-md ${
                                option.code === correctOption?.code
                                  ? 'bg-green-50 border-green-200'
                                  : option.code === userAnswer && !isCorrect
                                    ? 'bg-red-50 border-red-200'
                                    : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-center">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 text-xs ${
                                  option.code === correctOption?.code
                                    ? 'bg-green-500 text-white'
                                    : option.code === userAnswer && !isCorrect
                                      ? 'bg-red-500 text-white'
                                      : 'bg-gray-300 text-gray-600'
                                }`}>
                                  {option.code}
                                </div>
                                <div className="flex-1">
                                  {option.content}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* 答案解析 */}
                        {question.comments && (
                          <div className="mt-2 bg-yellow-50 p-3 rounded-md text-sm text-yellow-800 border border-yellow-100">
                            <div className="font-medium mb-1">解析：</div>
                            <div>{question.comments}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center text-gray-500">
                {showWrongOnly ? '没有错题记录' : '没有答题记录'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExamAnswerDetail; 